(() => {
  const dropdownSelector = [
    'details.collection-sort',
    'details.collection-category',
    'details.custom-wishlist__list-dropdown',
    'details.filter-group',
    'details.product-card__quick-add',
    'details.product-sticky-atc__custom-select',
    'details.yotpo-review-modal__custom-select',
    'details.site-footer__menu-column',
    'details.site-header-v2__currency-selector'
  ].join(',');
  const footerMenuMedia = window.matchMedia('(max-width: 749px)');
  const touchHandledSummaries = new WeakSet();
  let footerCurrencyPositionFrame = 0;

  const getDropdown = (target) => {
    if (!(target instanceof Element)) return null;

    const dropdown = target.closest(dropdownSelector);

    if (dropdown?.matches('.site-footer__menu-column') && !footerMenuMedia.matches) return null;

    return dropdown;
  };

  const getOpenDropdowns = () => {
    return Array.from(document.querySelectorAll(`${dropdownSelector}[open]`)).filter((dropdown) => {
      return !dropdown.matches('.site-footer__menu-column') || footerMenuMedia.matches;
    });
  };

  const syncFooterMenus = () => {
    document.querySelectorAll('details.site-footer__menu-column').forEach((menu) => {
      menu.open = !footerMenuMedia.matches;
    });
  };

  const closeDropdown = (dropdown) => {
    if (!dropdown?.open) return;
    dropdown.open = false;
    syncDropdownPosition(dropdown);
  };

  const closeDropdowns = ({ except = null } = {}) => {
    getOpenDropdowns().forEach((dropdown) => {
      if (dropdown !== except) closeDropdown(dropdown);
    });
  };

  const syncFooterCurrencyPosition = (dropdown) => {
    if (!(dropdown instanceof HTMLDetailsElement) || !dropdown.matches('.site-footer__currency-selector')) return;

    const summary = dropdown.querySelector('summary');
    const panel = dropdown.querySelector('.site-header-v2__currency-panel');

    if (!(summary instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

    dropdown.classList.remove('site-footer__currency-selector--drop-up');
    dropdown.classList.add('site-footer__currency-selector--drop-down');

    if (!dropdown.open) {
      dropdown.style.removeProperty('--site-footer-currency-panel-max-height');
      return;
    }

    const summaryRect = summary.getBoundingClientRect();
    const panelHeight = panel.getBoundingClientRect().height || panel.scrollHeight;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const offset = 12;
    const spaceAbove = Math.max(0, summaryRect.top - offset);
    const spaceBelow = Math.max(0, viewportHeight - summaryRect.bottom - offset);
    const shouldDropUp = spaceBelow < panelHeight && spaceAbove > spaceBelow;
    const availableSpace = Math.max(120, shouldDropUp ? spaceAbove : spaceBelow);

    dropdown.classList.toggle('site-footer__currency-selector--drop-up', shouldDropUp);
    dropdown.classList.toggle('site-footer__currency-selector--drop-down', !shouldDropUp);
    dropdown.style.setProperty('--site-footer-currency-panel-max-height', `${availableSpace}px`);
  };

  const syncYotpoReviewDropdownPosition = (dropdown) => {
    if (!(dropdown instanceof HTMLDetailsElement) || !dropdown.matches('.yotpo-review-modal__custom-select')) return;

    const summary = dropdown.querySelector('summary');
    const panel = dropdown.querySelector('.yotpo-review-modal__custom-select-panel');
    const scrollContainer = dropdown.closest('.yotpo-review-modal__form');

    dropdown.classList.remove('yotpo-review-modal__custom-select--drop-up');

    if (!dropdown.open) {
      dropdown.style.removeProperty('--yotpo-review-dropdown-max-height');
      return;
    }

    if (!(summary instanceof HTMLElement) || !(panel instanceof HTMLElement) || !(scrollContainer instanceof HTMLElement)) return;

    const summaryRect = summary.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    const offset = 8;
    const spaceAbove = Math.max(0, summaryRect.top - containerRect.top - offset);
    const spaceBelow = Math.max(0, containerRect.bottom - summaryRect.bottom - offset);
    const preferredHeight = Math.min(panel.scrollHeight, 240);
    const shouldDropUp = spaceBelow < preferredHeight && spaceAbove > spaceBelow;
    const availableSpace = Math.max(96, shouldDropUp ? spaceAbove : spaceBelow);

    dropdown.classList.toggle('yotpo-review-modal__custom-select--drop-up', shouldDropUp);
    dropdown.style.setProperty('--yotpo-review-dropdown-max-height', `${Math.min(preferredHeight, availableSpace)}px`);
  };

  const syncDropdownPosition = (dropdown) => {
    syncFooterCurrencyPosition(dropdown);
    syncYotpoReviewDropdownPosition(dropdown);
  };

  const syncOpenFooterCurrencyPositions = () => {
    document
      .querySelectorAll('details.site-footer__currency-selector[open]')
      .forEach(syncFooterCurrencyPosition);
  };

  const syncOpenYotpoReviewDropdownPositions = () => {
    document
      .querySelectorAll('details.yotpo-review-modal__custom-select[open]')
      .forEach(syncYotpoReviewDropdownPosition);
  };

  const requestOpenFooterCurrencyPositionSync = () => {
    if (footerCurrencyPositionFrame) {
      window.cancelAnimationFrame(footerCurrencyPositionFrame);
    }

    footerCurrencyPositionFrame = window.requestAnimationFrame(() => {
      footerCurrencyPositionFrame = 0;
      syncOpenFooterCurrencyPositions();
      syncOpenYotpoReviewDropdownPositions();
    });
  };

  const submitCurrencyOption = (target) => {
    const option = target instanceof Element
      ? target.closest('[data-header-currency-option]')
      : null;

    if (!(option instanceof HTMLElement)) return false;

    const form = option.closest('form.site-header-v2__currency-form');
    const input = form?.querySelector('[data-header-currency-input]');
    const countryCode = option.dataset.value;
    const dropdown = option.closest('details.site-header-v2__currency-selector');

    if (dropdown instanceof HTMLDetailsElement) closeDropdown(dropdown);

    if (!(form instanceof HTMLFormElement) || !(input instanceof HTMLInputElement) || !countryCode) {
      return true;
    }

    if (input.value === countryCode) return true;

    input.value = countryCode;
    HTMLFormElement.prototype.submit.call(form);

    return true;
  };

  const isCoarsePointerEvent = (event) => {
    return event.pointerType === 'touch' ||
      event.pointerType === 'pen' ||
      window.matchMedia('(hover: none), (pointer: coarse)').matches;
  };

  const getProductQuickAddSummary = (target) => {
    if (!(target instanceof Element)) return null;

    const summary = target.closest('details.product-card__quick-add > summary');

    return summary instanceof HTMLElement ? summary : null;
  };

  document.addEventListener('pointerdown', (event) => {
    if (!isCoarsePointerEvent(event)) return;

    const summary = getProductQuickAddSummary(event.target);
    const dropdown = summary?.parentElement;

    if (!(dropdown instanceof HTMLDetailsElement)) return;

    event.preventDefault();

    const shouldOpen = !dropdown.open;
    closeDropdowns({ except: shouldOpen ? dropdown : null });
    dropdown.open = shouldOpen;
    touchHandledSummaries.add(summary);
    summary.focus({ preventScroll: true });
  }, true);

  document.addEventListener('click', (event) => {
    if (submitCurrencyOption(event.target)) {
      event.preventDefault();
      return;
    }

    const summary = getProductQuickAddSummary(event.target);

    if (summary && touchHandledSummaries.has(summary)) {
      event.preventDefault();
      touchHandledSummaries.delete(summary);
      return;
    }

    const dropdown = getDropdown(event.target);

    closeDropdowns({ except: dropdown });
  });

  document.addEventListener('toggle', (event) => {
    const dropdown = event.target;

    if (!(dropdown instanceof HTMLDetailsElement) || !dropdown.matches(dropdownSelector) || !dropdown.open) return;
    if (dropdown.matches('.site-footer__menu-column') && !footerMenuMedia.matches) return;

    closeDropdowns({ except: dropdown });
    window.requestAnimationFrame(() => syncDropdownPosition(dropdown));
  }, true);

  window.addEventListener('resize', requestOpenFooterCurrencyPositionSync);
  window.addEventListener('scroll', requestOpenFooterCurrencyPositionSync, { passive: true });
  document.addEventListener('scroll', requestOpenFooterCurrencyPositionSync, { capture: true, passive: true });
  footerMenuMedia.addEventListener('change', syncFooterMenus);

  document.addEventListener('change', (event) => {
    const selectDropdown = event.target instanceof Element
      ? event.target.closest('details.collection-sort')
      : null;

    if (selectDropdown) closeDropdown(selectDropdown);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const activeDropdown = getDropdown(document.activeElement);

    if (activeDropdown?.open) {
      event.preventDefault();
      closeDropdown(activeDropdown);
      activeDropdown.querySelector('summary')?.focus();
      return;
    }

    closeDropdowns();
  });

  syncFooterMenus();
})();
